import { joinURL, withQuery } from 'ufo'
import { isDevelopment } from 'std-env'
import type { ArticleCardProps } from '../components/ArticleCard/ArticleCard.vue'
import { useSearchParams } from './data-fetching/useSearchParams'
import { useCapturedUtmSource } from './useCapturedUtmSource'
import { getUtmSource } from '../utils/tenants/getUtmSource'
import {
  QUARTZ_DISCOVER_URL,
  QUARTZ_REFERRER_POLICY,
} from '../utils/tenants/quartz'
import { getTypeTag } from './analytics/helper'

export const useRedirectActions = () => {
  const { selectedCategory, selectedLang } = useSearchParams()
  const { capturedUtmSource } = useCapturedUtmSource()
  const navigationOptions = { open: { target: '_blank' } }
  const appConfig = useAppConfig()
  const layoutSettings = useGlobalLayoutSettings()
  const { data: siteSettings } = storeToRefs(useSiteSettingsStore())

  function isQuartzArticle(article: ArticleCardProps) {
    return article.publisherName?.toLowerCase() === 'quartz'
  }

  // Quartz articles always open the QZ page with the tenant as utm_source.
  function buildQuartzUrl(articleUrl: string) {
    const url = new URL(articleUrl)
    if (!isDevelopment) {
      url.searchParams.set('utm_source', getUtmSource())
      url.searchParams.set('referrer', QUARTZ_DISCOVER_URL)
    }
    return url.toString()
  }

  function openQuartzArticle(articleUrl: string) {
    const link = document.createElement('a')
    link.href = articleUrl
    link.target = '_blank'
    link.rel = 'noopener'
    link.referrerPolicy = QUARTZ_REFERRER_POLICY
    link.click()
  }

  function getNewsArticleUrl(item: ArticleCardProps) {
    if (!item.id) return

    const { settings } = layoutSettings
    const { articleSummary } = useUserSettings(siteSettings)

    const pageUrl =
      item.type === 'video' ? settings.videoPageUrl : settings.articlePageUrl
    const path = joinURL(pageUrl, item.id)
    const query: Record<string, string | string[] | undefined> = {
      category: selectedCategory.value,
      lang: selectedLang.value,
    }
    if (capturedUtmSource.value) {
      query.utm_source = capturedUtmSource.value
    }

    if (item.type === 'video') return withQuery(path, query)
    if (isQuartzArticle(item) && item.articleUrl) {
      return buildQuartzUrl(item.articleUrl)
    }
    if (
      item.type !== 'game' &&
      item.type !== 'video' &&
      articleSummary.isEnabled
    ) {
      return withQuery(path, query)
    }
    return item.articleUrl
  }

  function navigateToContentPage(
    id: string | undefined,
    category: 'article' | 'video'
  ) {
    if (!id) return
    const categorySubPath =
      category === 'article' ? appConfig.urls.article : appConfig.urls.video
    const url = joinURL('/', categorySubPath, id)
    navigateTo(withQuery(url, { lang: selectedLang.value }))
  }

  async function sendToNewsPage(article: ArticleCardProps) {
    if (!article.id) return
    let navigationUrl = getNewsArticleUrl(article)

    // Append Discover → QZ tracking params before navigating to Quartz articles.
    if (!isDevelopment && isQuartzArticle(article) && navigationUrl) {
      const url = new URL(navigationUrl)
      url.searchParams.set('type_tag', await getTypeTag())
      navigationUrl = url.toString()
    }

    const event = new CustomEvent('article-click', {
      detail: { url: navigationUrl },
      cancelable: true,
    })
    window.dispatchEvent(event)
    if (event.defaultPrevented) return

    if (isQuartzArticle(article) && navigationUrl) {
      openQuartzArticle(navigationUrl)
      return
    }

    navigateTo(navigationUrl, navigationOptions)
  }

  function navigateToHomepage(
    category = 'News',
    lang: string | string[] | undefined
  ) {
    const homePageUrl = appConfig.urls.home
    const url = withQuery(homePageUrl, { category, lang })
    navigateTo(url)
  }

  function navigateToSearchPage(query: string) {
    if (!query) return
    let url = `/search/?q=${query}`
    if (selectedLang.value) {
      url += `&lang=${selectedLang.value}`
    }
    navigateTo(url)
  }

  return {
    sendToNewsPage,
    getNewsArticleUrl,
    navigateToHomepage,
    navigateToSearchPage,
    navigateToContentPage,
  }
}
